import type { ModelProduct, ModelSelectProducts } from '@app/api/Products';
import type {
  CategoryId,
  CategoryTreeNode,
  ProductTreeLeaf,
} from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/types';
import { makeProductTitle } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/utils';

const normalizeCategoryName = (value?: string | null): string | null => {
  const name = (value ?? '').trim();
  return name ? name : null;
};

const resolveCategoryTitle = (catId: CategoryId, products: ModelProduct[]) => {
  if (catId === null) return 'Без категории';

  // Берём самое частое непустое название (на случай рассинхрона на бэке)
  const counts = new Map<string, number>();

  for (const product of products) {
    const name = normalizeCategoryName(product.ProductCategory);
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }

  let bestName: string | null = null;
  let bestCount = 0;

  for (const [name, count] of counts.entries()) {
    if (count > bestCount) {
      bestName = name;
      bestCount = count;
    }
  }

  return bestName ?? `Категория ${catId}`;
};

export const groupProductsByCategory = (
  list: ModelProduct[],
  warehouseProductsMap?: Map<number, ModelSelectProducts>
): CategoryTreeNode[] => {
  const groups = new Map<CategoryId, ModelProduct[]>();

  list.forEach((p) => {
    const key = p.ProductCategoryId ?? null;
    const bucket = groups.get(key);
    if (bucket) bucket.push(p);
    else groups.set(key, [p]);
  });

  const nodes = Array.from(groups.entries()).map<CategoryTreeNode>(
    ([catId, products]) => ({
      key: `cat-${catId ?? 'none'}`,
      title: resolveCategoryTitle(catId, products),
      children: products
        .sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''))
        .map<ProductTreeLeaf>((p) => ({
          key: `cat-${catId ?? 'none'}-prod-${p.Id}`,
          isLeaf: true,
          title: makeProductTitle(p, warehouseProductsMap),
          dataRef: p,
        })),
    })
  );

  // Стабильный порядок: по названию, "Без категории" обычно сверху/снизу — при желании можно отдельно вынести
  return nodes.sort((a, b) =>
    (a.title ?? '').localeCompare(b.title ?? '', 'ru')
  );
};
